import React, { useMemo } from 'react';
import ViolationStateBlock from '../components/ViolationStateBlock';
import DetectionCapabilityRow from './DetectionCapabilityRow';
import DetectorDependencyConfirmModal from './DetectorDependencyConfirmModal';
import useDetectionProfile from './useDetectionProfile';
import {
  ANALYTICS_MODE_OPTIONS,
  deriveMasterState,
  getFeatureDisplayName,
} from '../../../constants/analyticsConstants';

/**
 * Профиль детекции камеры — «Режим аналитики» и «Детекции» (Part 4, разделы
 * 4 и 6.2–6.3; ТЗ задачи 21, 22, 26).
 *
 * Извлечено из AnalyticsTab, чтобы Task 14 (Admin → Edit Camera) встроил
 * тот же UI поверх того же useDetectionProfile — второго стора для профиля
 * детекции быть не должно.
 *
 * Компонент admin-only по конструкции: профиль детекции живёт на admin-роуте,
 * и вызывающий код обязан рендерить секцию только когда пользователь —
 * администратор (см. AnalyticsTab: `{admin && <DetectionProfileSection .../>}`).
 */
const DetectionProfileSection = ({ cameraId, onRulesChanged }) => {
  const {
    profile,
    phase,
    errorMessage,
    actionError,
    notice,
    busyFeatureId,
    pendingForce,
    load,
    toggleManual,
    setMaster,
    confirmForce,
    cancelForce,
  } = useDetectionProfile(cameraId, { onRulesChanged });

  // '__master__' — признак того, что текущий pendingForce пришёл от
  // master-переключателя «Все детекции», а не от отдельной фичи.
  const isMasterFlow = pendingForce?.featureId === '__master__';

  const pendingFeatureName = useMemo(() => {
    if (!pendingForce || !profile || isMasterFlow) return '';
    const feature = profile.features.find((item) => item.feature_id === pendingForce.featureId);
    return feature ? getFeatureDisplayName(feature) : pendingForce.featureId;
  }, [pendingForce, profile, isMasterFlow]);

  if (phase === 'loading') return <ViolationStateBlock state="loading" />;
  if (phase === 'error') {
    return <ViolationStateBlock state="error" message={errorMessage} onRetry={load} />;
  }

  if (!profile) return null;

  const features = Array.isArray(profile.features) ? profile.features : [];
  const master = deriveMasterState(profile);

  return (
    <>
      <div className="vio-card">
        <p className="vio-drawer-section-title">Режим аналитики</p>
        <div className="vio-analytics-mode">
          {ANALYTICS_MODE_OPTIONS.map((option) => (
            <label key={option.value} className="vio-radio">
              <input
                type="radio"
                name="vio-analytics-mode"
                value={option.value}
                checked={profile.analytics_mode === option.value}
                readOnly
                disabled
              />
              {option.label}
            </label>
          ))}
        </div>
      </div>

      <div className="vio-card">
        <p className="vio-drawer-section-title">Детекции</p>

        <div className="vio-master-toggle">
          <label className="vio-checkbox">
            <input
              type="checkbox"
              ref={(el) => { if (el) el.indeterminate = master === 'partial'; }}
              checked={master === 'on'}
              disabled={master === 'none' || Boolean(busyFeatureId)}
              onChange={(event) => setMaster(event.target.checked)}
            />
            Все детекции
          </label>
          <span className="vio-field-hint">
            {master === 'on' && 'Все доступные детекции включены вручную'}
            {master === 'off' && 'Ручное включение детекций выключено'}
            {master === 'partial' && 'Включена часть детекций'}
          </span>
        </div>

        {actionError && <p className="vio-field-error">{actionError}</p>}
        {notice && <p className="vio-notice">{notice}</p>}

        {features.length === 0 ? (
          <ViolationStateBlock state="empty" message="Для этой камеры детекции не заданы." />
        ) : (
          <ul className="vio-feature-list">
            {features.map((feature) => (
              <DetectionCapabilityRow
                key={feature.feature_id}
                feature={feature}
                profile={profile}
                busy={busyFeatureId === feature.feature_id}
                disabled={Boolean(busyFeatureId) && busyFeatureId !== feature.feature_id}
                onToggleManual={toggleManual}
              />
            ))}
          </ul>
        )}
      </div>

      {pendingForce && (
        <DetectorDependencyConfirmModal
          featureName={pendingFeatureName}
          dependentRules={pendingForce.dependentRules}
          busy={Boolean(busyFeatureId)}
          error={pendingForce.error}
          title={isMasterFlow ? 'Выключить все детекции?' : undefined}
          intro={isMasterFlow ? 'Следующие правила используют выключаемые детекции и будут деактивированы:' : undefined}
          confirmLabel={isMasterFlow ? 'Выключить детекции и деактивировать зависимые правила' : undefined}
          onConfirm={confirmForce}
          onCancel={cancelForce}
        />
      )}
    </>
  );
};

export default DetectionProfileSection;
